import {Component, Input, OnChanges, OnDestroy, QueryList, SimpleChanges, ViewChildren} from '@angular/core';
import {TranslateService} from '@ngx-translate/core';
import {TableHeaderSortableService} from '@services/table-header-sortable/table-header-sortable.service';
import {LoadingIndicatorService} from '@services/loading-indicator/loading-indicator.service';
import {NotificationService} from '@services/notification/notification.service';
import {ArrayOperationService} from '@services/administration/array-operation/array-operation.service';
import {TriggerQueryService} from '@services/trigger/triger-action/trigger-query.service';
import {UtilsService} from '@services/utils/utils.service';
import {SortEvent, TableHeaderSortableDirective} from '@shared/table-header-sortable/table-header-sortable.directive';
import {
  TriggerQueryEditComponent
} from '@components/administration/machine/machine-configuration/trigger/trigger-query/modals/trigger-query-edit/trigger-query-edit.component';
import {Trigger} from '@models/adminstration/machine/trigger/trigger';
import {TriggerQuery} from '@models/adminstration/machine/trigger/trigger-query/trigger-query';
import {Signal} from '@models/dashboard/signal';
import {Role} from '@models/user/role';
import {User} from '@models/user/user';
import {TriggerHelperService} from '@services/trigger/helper/trigger-helper.service';
import {TRIGGER_ACTION_TYPE} from '@constants/trigger/trigger-action-type.constant';
import {DEFAULT_SORT_TYPE} from '@constants/sorting/default-sort-type';
import {ModalService} from '@services/modal/modal.service';
import {Subject} from 'rxjs';
import {takeUntil} from 'rxjs/operators';

@Component({
    selector: 'scitis-admin-machine-trigger-query',
    templateUrl: './trigger-query.component.html',
    styleUrls: ['./trigger-query.component.scss'],
    standalone: false
})
export class TriggerQueryComponent implements OnChanges, OnDestroy {

  @Input()
  machineId: number | undefined = undefined;

  @Input()
  signals: Signal[] = new Array<Signal>();

  @Input()
  userRole: Role = new Role(new User());

  triggerQueriesList: Trigger[] = new Array<Trigger>();
  @ViewChildren(TableHeaderSortableDirective) sortableHeaders: QueryList<TableHeaderSortableDirective> = new QueryList<TableHeaderSortableDirective>();
  private CURRENT_PAGE_NAME = 'TriggerAction';
  private COMPONENT_NAME: string = 'Trigger Query Edit';
  private destroy: Subject<boolean> = new Subject<boolean>();

  constructor(
      private loadingIndicator: LoadingIndicatorService,
      private translation: TranslateService,
      private notificationService: NotificationService,
      private tableHeaderSortableService: TableHeaderSortableService,
      private modalService: ModalService,
      private arrayOperationService: ArrayOperationService,
      private triggerActionService: TriggerQueryService,
      private triggerHelperService: TriggerHelperService,
      private utilsService: UtilsService,
  ) {
  }


  ngOnChanges(changes: SimpleChanges): void {
    if (!changes) {
      return;
    }

    if (changes.machineId && changes.machineId.currentValue) {
      this.setTriggerQueries();
    }
  }

  public onAddTriggerQuery(): void {
    const modalRef = this.modalService.open(TriggerQueryEditComponent, {size: 'lg'});
    const parentId = this.machineId !== undefined ? this.machineId : -1;
    modalRef.componentInstance.triggerQuery = new TriggerQuery(parentId);
    modalRef.componentInstance.signals = this.signals;
    modalRef.componentInstance.userRole = this.userRole;
    modalRef.result.then(({request, parentId}) => {
      this.loadingIndicator.show(this.translation.instant('Loading'));
      this.triggerActionService.addTrigger(parentId, request).pipe(takeUntil(this.destroy)).subscribe({
        next: (trigger: Trigger) => {
          this.triggerQueriesList.push(trigger);
          this.onColumnSort(DEFAULT_SORT_TYPE);
          const message = this.translation.instant('Successfully created/updated trigger action');
          this.notificationService.success(message, this.COMPONENT_NAME, false);
          this.loadingIndicator.hide();
        }, error: error => {
          console.log(error);
          this.loadingIndicator.hide();
          const message = this.translation.instant('Error creating/updating trigger action');
          this.notificationService.error(message, error, this.COMPONENT_NAME, true);
        }
      });
    }).catch(() => {
    });
  }

  public onColumnSort({column, direction}: SortEvent): void {
    this.sortableHeaders = this.tableHeaderSortableService.resetHeaders(this.sortableHeaders, column);
    this.triggerQueriesList = this.tableHeaderSortableService.sortItems<Trigger>(this.triggerQueriesList, column, direction);
  }

  public onDeleteEvent(triggerQuery: Trigger): void {
    this.arrayOperationService.deleteItem(triggerQuery, this.triggerQueriesList, 'id');
    this.onColumnSort(DEFAULT_SORT_TYPE);
  }

  public getBadgeClass(triggerActionType: TRIGGER_ACTION_TYPE): string {
    return this.triggerHelperService.getBadgeClass(triggerActionType);
  }

  getTrigger(trigger: Trigger): Trigger {
    return this.utilsService.getObjectDeepCopy(trigger);
  }

  onTriggerQueryUpdatedEvent(newTrigger: Trigger): void {
    let found = false;
    let index = 0;
    this.triggerQueriesList.forEach(trigger => {
      if (trigger.id === newTrigger.id) {
        found = true;
        this.triggerQueriesList[index] = newTrigger;
      }
      index++;
    });
    if (!found) {
      this.triggerQueriesList.push(newTrigger);
    }
  }

  private setTriggerQueries(): void {
    if (this.machineId !== undefined) {
      this.loadingIndicator.show(this.translation.instant('Loading'));
      this.triggerActionService.triggerQuery(this.machineId).pipe(takeUntil(this.destroy)).subscribe({
        next: (triggerQueries: Trigger[]) => {
          this.triggerQueriesList = triggerQueries;
          this.onColumnSort(DEFAULT_SORT_TYPE);
          this.loadingIndicator.hide();
        }, error: error => {
          console.log(error);
          this.loadingIndicator.hide();
          const message = this.translation.instant('TriggerAction. error loading triggerQueries');
          this.notificationService.error(message, error, this.CURRENT_PAGE_NAME, true);
        }
      });
    }
  }

  ngOnDestroy(): void {
    this.destroy.next(true);
    this.destroy.complete();
  }
}
